import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

const PANEL_WIDTH = 300;
const COLLAPSED_WIDTH = 40;

const products = [
  {
    name: "cilika",
    color: "#4CAF50",
    logo: process.env.PUBLIC_URL + "/cilika-logo.webp",
    image: process.env.PUBLIC_URL + "/cilika-product.webp",
    title: "CILIKA Digital Microscopes",
    description:
      "Tablet and smartphone-integrated digital microscope with patented technology. 100% Field of View, zero lag, and ergonomic design for clinical, research, and educational use.",
    link: "https://www.medprimetech.com/cilika",
  },
  {
    name: "micalys",
    color: "#7C4DFF",
    logo: process.env.PUBLIC_URL + "/micalys-logo.webp",
    image: process.env.PUBLIC_URL + "/micalys-product.webp",
    title: "Whole Slide Scanner",
    description:
      "Automated whole slide scanner with 100x oil immersion, 11-layer Z-stacking, and built-in telepathology. Quick Share, Secure IP Sharing, and Live View with Robotic Remote Control.",
    link: "https://www.medprimetech.com/micalys",
  },
];

const EcosystemPanel = ({ open, onToggle }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexShrink: 0,
        borderLeft: "1px solid #e0e0e0",
        height: "100%",
        width: open ? PANEL_WIDTH : COLLAPSED_WIDTH,
        transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Collapsed strip — always rendered, fades in/out */}
      <Box
        onClick={!open ? onToggle : undefined}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: COLLAPSED_WIDTH,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 1.5,
          gap: 1.5,
          cursor: open ? "default" : "pointer",
          backgroundColor: "#fafafa",
          opacity: open ? 0 : 1,
          pointerEvents: open ? "none" : "auto",
          transition: "opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            backgroundColor: open ? "#fafafa" : "#f0f0f0",
          },
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 20, color: "#1718FF" }} />
        <HubOutlinedIcon sx={{ fontSize: 18, color: "#1718FF" }} />
        <Typography
          sx={{
            writingMode: "vertical-rl",
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 500,
            fontSize: "0.7rem",
            color: "#616161",
            letterSpacing: "0.05em",
          }}
        >
          Ecosystem
        </Typography>
      </Box>

      {/* Expanded panel — always rendered, fades in/out */}
      <Box
        sx={{
          width: PANEL_WIDTH,
          minWidth: PANEL_WIDTH,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#ffffff",
          overflowY: "auto",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <HubOutlinedIcon sx={{ fontSize: 20, color: "#1718FF" }} />
            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                fontSize: "0.95rem",
                color: "#1d1d1f",
                flex: 1,
                whiteSpace: "nowrap",
              }}
            >
              Explore Our Ecosystem
            </Typography>
            <IconButton
              onClick={onToggle}
              size="small"
              sx={{
                width: 28,
                height: 28,
                color: "#9e9e9e",
                "&:hover": { color: "#1718FF", backgroundColor: "rgba(23, 24, 255, 0.04)" },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 500,
              fontSize: "0.7rem",
              color: "#9e9e9e",
              pl: 0,
              whiteSpace: "nowrap",
            }}
          >
            Integrated digital pathology solutions
          </Typography>
        </Box>

        {/* Product cards */}
        {products.map((product) => (
          <Box key={product.name} sx={{ px: 2.5, py: 2, borderTop: "1px solid #f0f0f0" }}>
            {/* Brand logo */}
            <Box sx={{ mb: 1.5 }}>
              <img
                src={product.logo}
                alt={product.name}
                style={{ height: 28, objectFit: "contain" }}
              />
            </Box>

            {/* Product image */}
            <Box
              sx={{
                backgroundColor: "#f8f8f8",
                borderRadius: "8px",
                height: 120,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1.5,
                overflow: "hidden",
              }}
            >
              <img
                src={product.image}
                alt={product.title}
                style={{
                  maxHeight: "100%",
                  maxWidth: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>

            {/* Title */}
            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 500,
                fontSize: "0.8rem",
                color: "#1d1d1f",
                mb: 0.75,
              }}
            >
              {product.title}
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 500,
                fontSize: "0.7rem",
                color: "#616161",
                lineHeight: 1.6,
                mb: 1.5,
              }}
            >
              {product.description}
            </Typography>

            {/* Know More button */}
            <Button
              variant="outlined"
              endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
              href={product.link}
              target="_blank"
              rel="noopener noreferrer"
              fullWidth
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 400,
                fontSize: "0.75rem",
                textTransform: "none",
                borderColor: "#1718FF",
                color: "#1718FF",
                borderRadius: "8px",
                py: 1,
                whiteSpace: "nowrap",
                "&:hover": {
                  borderColor: "#1718FF",
                  backgroundColor: "rgba(23, 24, 255, 0.04)",
                },
              }}
            >
              Know More
            </Button>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default EcosystemPanel;
